const markerSelector = "[data-navigation-ready]";

type NavigationMarker = {
  readonly marker: HTMLElement;
  readonly hydrated: boolean;
};

export class NavigationMarkerObserver {
  private readonly markers = new Set<HTMLElement>();
  private readonly hydrated = new WeakSet<HTMLElement>();
  private readonly addedRoots = new Set<Element>();
  private readonly mutations: MutationObserver;
  private readonly onInspect: () => void;
  private frame = 0;

  constructor({ onInspect }: { readonly onInspect: () => void }) {
    this.onInspect = onInspect;
    this.mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) this.addedRoots.add(node);
        }
      }

      this.schedule();
    });
  }

  private collect(root: Element) {
    if (root instanceof HTMLElement && root.matches(markerSelector))
      this.markers.add(root);

    for (const marker of root.querySelectorAll<HTMLElement>(markerSelector))
      this.markers.add(marker);
  }

  start() {
    this.collect(document.documentElement);
    this.mutations.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "hidden"],
    });
  }

  stop() {
    this.mutations.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.addedRoots.clear();
  }

  clear() {
    this.markers.clear();
  }

  schedule() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.onInspect();
    });
  }

  register(marker: HTMLElement) {
    this.hydrated.add(marker);
    this.markers.add(marker);
    return () => {
      this.hydrated.delete(marker);
      this.markers.delete(marker);
    };
  }

  flush(onMarker: (entry: NavigationMarker) => void) {
    for (const root of this.addedRoots) {
      if (root.isConnected) this.collect(root);
    }

    this.addedRoots.clear();

    for (const marker of this.markers) {
      if (!marker.isConnected) {
        this.markers.delete(marker);
        continue;
      }

      onMarker({ marker, hydrated: this.hydrated.has(marker) });
    }
  }
}
